import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Truck,
  Boxes,
  Warehouse,
  Wrench,
  Building2,
  MessageSquare,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useSettings } from "../lib/settings";
import { Reveal, SectionHeading } from "../components/Reveal";
import { Watermark } from "../components/Gunungan";
import TruckStrip from "../components/TruckStrip";
import { WhatsAppButton } from "../components/WhatsApp";
import { generalMessage } from "../lib/wa";
import { KATEGORI, KATEGORI_ORDER, KATEGORI_META } from "../lib/utils";

const STATS = [
  { value: "21+", label: "Unit armada siap operasional" },
  { value: "4", label: "Kategori armada" },
  { value: "Siap", label: "Mendukung pengiriman barang Anda" },
  { value: "Tim", label: "Komunikasi langsung dengan tim" },
];

const SERVICES = [
  {
    icon: Truck,
    title: "Layanan Logistik & Pengiriman Barang",
    desc: "Layanan pengiriman barang untuk mendukung operasional dan kebutuhan bisnis Anda.",
  },
  {
    icon: Boxes,
    title: "Dukungan Transportasi Distribusi",
    desc: "Armada yang mendukung kegiatan distribusi barang dan kelancaran aktivitas bisnis Anda.",
  },
  {
    icon: Warehouse,
    title: "Untuk Pengusaha, Gudang & Perusahaan",
    desc: "Melayani kebutuhan pengiriman pelanggan bisnis — dari pengusaha dan pengelola gudang hingga perusahaan.",
  },
  {
    icon: Wrench,
    title: "Solusi Transportasi Sesuai Kebutuhan",
    desc: "Diskusikan kebutuhan pengiriman Anda — tim kami bantu siapkan dukungan armada yang sesuai.",
  },
];

const WHY = [
  {
    icon: ShieldCheck,
    title: "Armada Terawat",
    desc: "Armada dirawat dan dijaga kesiapannya agar selalu siap mendukung kebutuhan pengiriman Anda.",
  },
  {
    icon: Users,
    title: "Driver Berpengalaman",
    desc: "Setiap armada dikemudikan driver yang terbiasa melayani kebutuhan pengiriman bisnis.",
  },
  {
    icon: Building2,
    title: "Melayani Kebutuhan Bisnis",
    desc: "Siap melayani pengusaha, pengelola gudang, dan perusahaan dengan kebutuhan pengangkutan yang berbeda.",
  },
  {
    icon: MessageSquare,
    title: "Komunikasi Langsung dengan Tim",
    desc: "Konsultasikan kebutuhan pengiriman Anda langsung dengan tim kami.",
  },
];

function Hero() {
  const line = {
    hidden: { y: "115%" },
    show: (i) => ({
      y: "0%",
      transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
    }),
  };
  return (
    <section className="relative flex min-h-[74svh] items-start overflow-hidden border-b border-zinc-800 bg-white">
      <div className="wrap relative pb-14 pt-8 sm:pt-10">
        <h1 className="mt-2 font-display font-black uppercase leading-[0.92] tracking-tight text-[clamp(2.4rem,8.5vw,6.4rem)]">
          {["Perusahaan", "Transportasi", "Layanan Logistik."].map((l, i) => (
            <span key={l} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={line}
                initial="hidden"
                animate="show"
                className={`block ${i === 2 ? "text-gold" : "text-[#1a1a1a]"}`}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#1a1a1a]"
          data-testid="hero-subheadline"
        >
          Putra Bengawan Logistik menyediakan layanan logistik dan pengiriman barang untuk mendukung
          kebutuhan distribusi serta transportasi bisnis Anda.
        </motion.p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-800 md:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-white px-6 py-5">
              <p className="font-display text-3xl font-extrabold text-gold sm:text-4xl">{s.value}</p>
              <p className="mt-1 text-xs text-zinc-500 sm:text-sm">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Layanan() {
  return (
    <section id="layanan" className="border-b border-zinc-800 py-24">
      <div className="wrap">
        <SectionHeading
          eyebrow="Layanan"
          title="Layanan Kami"
          description="Layanan yang dirancang untuk kebutuhan pengiriman dan transportasi pelanggan bisnis."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={0.06 * i}>
              <div className="group h-full rounded-2xl border border-zinc-800 bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50">
                <s.icon className="h-7 w-7 text-gold" strokeWidth={1.75} />
                <h3 className="mt-5 font-display text-lg font-bold leading-snug text-navy">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Keunggulan() {
  return (
    <section className="border-b border-zinc-800 bg-ink-900/40 py-24">
      <div className="wrap">
        <SectionHeading eyebrow="Keunggulan" title={<>Kenapa Putra Bengawan Logistik?</>} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={0.06 * i}>
              <div className="h-full rounded-2xl border border-zinc-800 bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50">
                <w.icon className="h-6 w-6 text-gold" strokeWidth={1.75} />
                <h3 className="mt-4 font-display text-lg font-bold leading-snug text-navy">
                  {w.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArmadaKategori() {
  return (
    <section id="armada" className="wrap py-24">
      <SectionHeading
        eyebrow="Armada"
        title="Armada Kami"
        description="Empat kategori armada untuk mendukung kebutuhan pengiriman dan transportasi bisnis Anda."
      />
      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {KATEGORI_ORDER.map((k, i) => (
          <Reveal key={k} delay={0.06 * i}>
            <div
              data-testid={`kategori-card-${k}`}
              className="group h-full overflow-hidden rounded-2xl border border-zinc-800 bg-card transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
            >
              <div className="h-40 overflow-hidden">
                <img
                  src={KATEGORI_META[k].foto}
                  alt={KATEGORI[k]}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-extrabold uppercase text-navy">
                  {KATEGORI[k]}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                  {KATEGORI_META[k].descCard || KATEGORI_META[k].desc}
                </p>
                <Link
                  data-testid={`kategori-detail-${k}`}
                  to={`/armada/${k}`}
                  className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-zinc-700 px-4 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-gold-deep"
                >
                  Lihat Detail <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Kontak() {
  const settings = useSettings();
  return (
    <section id="kontak" className="relative overflow-hidden border-t border-zinc-800 py-24 sm:py-28">
      <Watermark className="pointer-events-none absolute right-[-70px] top-1/2 hidden h-[500px] w-auto -translate-y-1/2 text-navy opacity-[0.05] lg:block" />
      <div className="wrap relative">
        <SectionHeading
          eyebrow="Kontak"
          title={<>Butuh Layanan Pengiriman untuk Bisnis Anda?</>}
          description="Konsultasikan kebutuhan pengiriman barang dan transportasi bisnis Anda dengan tim kami — langsung melalui WhatsApp atau info kontak lengkap kami."
        />
        <Reveal delay={0.2} className="mt-10 flex flex-wrap gap-4">
          <WhatsAppButton
            settings={settings}
            message={generalMessage()}
            testId="home-kontak-wa"
            variant="wa"
            size="lg"
          >
            HUBUNGI KAMI
          </WhatsAppButton>
          <Link
            data-testid="home-kontak-link"
            to="/kontak"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-700 px-8 py-4 font-semibold text-navy transition-all hover:border-gold hover:text-gold-deep"
          >
            Info Kontak Lengkap <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <TruckStrip />
      <Hero />
      <Layanan />
      <Keunggulan />
      <ArmadaKategori />
      <Kontak />
    </>
  );
}
